import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "./AuthPage";

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [busyPlan, setBusyPlan] = useState("");
  const { user, logout } = useAuth();

  useEffect(() => {
    api.get("/payments/config").then((r) => setPlans(r.data.plans));
  }, []);

  const subscribe = async (lookup_key) => {
    setBusyPlan(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusyPlan("");
    }
  };

  const openPortal = async () => {
    setBusyPlan("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusyPlan("");
    }
  };

  const sub = user?.subscription || {};
  const active = sub.status === "active";

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-6">
      <div className="flex items-center gap-3">
        <img src="/mascot.png" alt="mascot" className="w-14 h-14 rounded-full border-4 border-black shadow-[3px_3px_0px_rgba(0,0,0,1)] bg-white object-cover" />
        <div>
          <h1 className="font-heading text-3xl font-semibold">Subscription</h1>
          <p className="text-gray-600 text-sm">StudySpark Family Plan</p>
        </div>
      </div>

      {active ? (
        <div className="space-y-4">
          <div className="bg-[#51CF66] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 flex items-center gap-3" data-testid="billing-active-card">
            <CheckCircle2 size={28} />
            <div>
              <p className="font-heading text-xl font-semibold">You're subscribed!</p>
              <p className="text-sm">Plan: {sub.plan === "spark_yearly" ? "Yearly (A$190/yr)" : "Monthly (A$19/mo)"}</p>
            </div>
          </div>
          <button onClick={openPortal} disabled={busyPlan === "portal"} data-testid="manage-subscription-button"
            className="w-full bg-white border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {busyPlan === "portal" && <Loader2 size={16} className="animate-spin" />} Manage or cancel subscription
          </button>
          <p className="text-xs text-gray-500 text-center">Opens the secure Stripe billing portal — update card, view invoices, or cancel.</p>
        </div>
      ) : sub.access ? (
        <div className="bg-[#FFE066] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5" data-testid="billing-trial-card">
          <p className="font-heading text-lg font-semibold flex items-center gap-2"><Sparkles size={18} /> Free access — {sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left</p>
          <p className="text-sm text-gray-700 mt-1">Lock in your subscription so Mia never misses a beat with {user?.child?.name || "your child"}.</p>
        </div>
      ) : sub.status === "cancelled" ? (
        <div className="bg-[#FF6B6B] text-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5" data-testid="billing-expired-card">
          <p className="font-heading text-lg font-semibold">Your subscription has ended</p>
          <p className="text-sm mt-1">Restart below to unlock Mia, focus sprints, study sets and the assignment calendar again.</p>
        </div>
      ) : (
        <div className="bg-[#4D96FF] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5" data-testid="billing-expired-card">
          <p className="font-heading text-lg font-semibold flex items-center gap-2"><Sparkles size={18} /> One last step — start your free 7-day trial</p>
          <p className="text-sm mt-1">$0 today. After 7 days it upgrades to your plan automatically — cancel anytime right here.</p>
        </div>
      )}

      {!active && (
        <div className="space-y-4">
          {plans.map((p) => (
            <div key={p.lookup_key} className={`border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 ${p.lookup_key === "spark_yearly" ? "bg-[#F2C6DE]" : "bg-white"}`} data-testid={`plan-card-${p.lookup_key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading text-xl font-semibold">{p.name}</p>
                  <p className="text-sm text-gray-700">{p.price} / {p.interval} · {p.note}</p>
                </div>
                <button onClick={() => subscribe(p.lookup_key)} disabled={!!busyPlan}
                  data-testid={`subscribe-${p.lookup_key}`}
                  className="bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-5 py-2.5 disabled:opacity-60 flex items-center gap-2">
                  {busyPlan === p.lookup_key && <Loader2 size={16} className="animate-spin" />} Start trial
                </button>
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500 text-center">Secure checkout by Stripe. $0 today — first charge after your 7 free days. Cancel anytime.</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4">
        {sub.access && (
          <Link to="/" className="font-bold underline text-sm" data-testid="billing-back-home">← Back to StudySpark</Link>
        )}
        <button onClick={logout} data-testid="billing-logout-button"
          className="flex items-center gap-1.5 text-sm font-bold text-gray-500 hover:text-black transition-colors">
          <LogOut size={15} /> Log out
        </button>
      </div>
    </div>
  );
}
